// Components
import Box from '@amzn/awsui-components-react/polaris/box';
import Button from '@amzn/awsui-components-react/polaris/button';
import Form from '@amzn/awsui-components-react/polaris/form';
import FormField from '@amzn/awsui-components-react/polaris/form-field';
import Input from '@amzn/awsui-components-react/polaris/input';
import Modal, { ModalProps } from '@amzn/awsui-components-react/polaris/modal';
import SpaceBetween from '@amzn/awsui-components-react/polaris/space-between';
// Hooks
import { useState } from 'react';
import { useAttendance, useTrackTime } from '../../hooks/attendance/attendanceMutation';
import {
  useParticipantSessionStorage,
  ParamsParticipantData,
} from '../../hooks/attendance/participantSessionStorage';
import { useBulkParticipantSessionStorage } from '../../hooks/attendance/bulkParticipantSessionStorage';
import { useTranslation } from 'react-i18next';
import { useUser } from '../../hooks/user';
// Types
import { Shift, AttendanceStatus } from '@amzn/red-velvet-api';

export interface TimeModalProps {
  shift: Shift;
  saveToUrl?: boolean;
  aliases?: string[];
  visible: boolean;
  onDismiss: NonNullable<ModalProps['onDismiss']>; // This makes the optional onDismiss function from the Modal props into a required field
}

const MIN_PER_HOUR = 60;
const MILLS_PER_MIN = 1000 * 60;
const MAX_SHIFT_HOURS = 12;
const SHORT_SHIFT_HOURS = 8;
const SHORT_SHIFT_ALLOWED_EXTRA_HOURS = 2;

export function formatMinutes(minutes: number) {
  const hours = Math.floor(minutes / MIN_PER_HOUR);
  const mins = minutes % MIN_PER_HOUR;

  return `${hours < 10 ? '0' + hours : hours}:${mins < 10 ? '0' + mins : mins}`;
}

export function parseTime(rawTime: string, shiftMins: number) {
  let hours = 0;
  let min = 0;

  let error = '';
  let warning = '';

  const timeChunks = rawTime.split(':');
  if (timeChunks) {
    if (timeChunks.length > 0) {
      hours = timeChunks[0] ? parseInt(timeChunks[0]) : 0;
    }

    if (timeChunks.length > 1) {
      min = timeChunks[1] ? parseInt(timeChunks[1]) : 0;
    }
  }

  if (hours === 0 && min === 0) {
    error = 'timeModal.noTime';
  }

  const minutesReported = hours * MIN_PER_HOUR + min;

  if (shiftMins !== 0 && minutesReported > MAX_SHIFT_HOURS * MIN_PER_HOUR) {
    error = 'timeModal.exceededMaxAttendanceTime';
  } else if (
    shiftMins !== 0 &&
    shiftMins <= SHORT_SHIFT_HOURS * MIN_PER_HOUR &&
    minutesReported - shiftMins >= SHORT_SHIFT_ALLOWED_EXTRA_HOURS * MIN_PER_HOUR
  ) {
    warning = 'timeModal.shortShiftHighTime';
  } else if (
    minutesReported > SHORT_SHIFT_HOURS * MIN_PER_HOUR &&
    minutesReported <= MAX_SHIFT_HOURS * MIN_PER_HOUR
  ) {
    warning = 'timeModal.longShiftHighTime';
  } else if (minutesReported > MAX_SHIFT_HOURS * MIN_PER_HOUR) {
    error = 'timeModal.exceededMaxShiftTime';
  } else if (min >= MIN_PER_HOUR) {
    hours += Math.floor(min / MIN_PER_HOUR);
    min = min % MIN_PER_HOUR;
    warning = 'timeModal.tooManySections';
  } else if (timeChunks.length > 2) {
    warning = 'timeModal.tooManySections';
  }

  return {
    hours,
    min,
    time: formatMinutes(minutesReported),
    minutesReported,
    error,
    warning,
  };
}

function useParsedTimeWithWarningsAndErrors(shiftMins: number) {
  const { t } = useTranslation(['shifts', 'translation']);

  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');

  return {
    parseTimeWithWarningsAndErrors: (rawTime: string) => {
      const parsed = parseTime(rawTime, shiftMins);

      if (parsed.error) {
        setError(t(parsed.error, { hours: parsed.hours, min: parsed.min }));
      } else {
        // We need to reset the error to a nullish value if there is no error
        // to ensure the modal works properly
        setError('');
      }
      if (parsed.warning) {
        setWarning(t(parsed.warning, { hours: parsed.hours, min: parsed.min }));
      } else {
        // We need to reset the warning to a nullish value if there is no warning
        // to ensure the modal works properly
        setWarning('');
      }

      return parsed.minutesReported;
    },
    error,
    setError,
    warning,
    setWarning,
  };
}

function useUpdateAttendance(
  shift: Shift,
  onDismiss: NonNullable<ModalProps['onDismiss']>,
  aliases: string[]
) {
  const { t } = useTranslation(['shifts', 'translation']);
  const user = useUser();
  const attendance = useAttendance(shift.eventId, shift.shiftId, user.userId);

  const isLongShift =
    shift.startDateTime &&
    shift.endDateTime &&
    shift.endDateTime.getTime() - shift.startDateTime.getTime() >
      SHORT_SHIFT_HOURS * MIN_PER_HOUR * MILLS_PER_MIN;

  const shiftMins =
    shift.startDateTime && shift.endDateTime && !isLongShift
      ? Math.floor((shift.endDateTime.getTime() - shift.startDateTime.getTime()) / MILLS_PER_MIN)
      : 0;

  const [time, setTime] = useState(
    attendance.data
      ? formatMinutes(attendance.data.minutes)
      : shiftMins
        ? formatMinutes(shiftMins)
        : ''
  );

  const timeMutation = useTrackTime(shift.eventId, shift.shiftId);

  const { error, setError, warning, parseTimeWithWarningsAndErrors } =
    useParsedTimeWithWarningsAndErrors(shiftMins);

  const { updateParticipants } = useParticipantSessionStorage();
  const { updateBulkParticipantsFromParticipantData } = useBulkParticipantSessionStorage();

  const updateAttendanceWithApi = () => {
    const minutes = parseTimeWithWarningsAndErrors(time);
    if (minutes !== 0) {
      timeMutation.mutate(
        {
          eventId: shift.eventId,
          shiftId: shift.shiftId,
          alias: user.userId,
          minutes,
          attendanceStatus: AttendanceStatus.ATTENDED,
        },
        {
          onError: (error) => {
            setError(t('timeModal.error', { error: error.message }));
          },
          onSuccess: () => {
            // Note: the 'closeButton' input here indicates that the modal was closed by an action within the modal
            // since the dismiss function only accepts the following reasons: 'closeButton', 'overlay', 'keyboard'
            // and overlay and keyboard indicate user events that are external to the modal
            onDismiss(new CustomEvent<ModalProps.DismissDetail>('closeButton'));
          },
        }
      );
    }
  };

  const updateAttendanceInUrl = () => {
    const minutes = parseTimeWithWarningsAndErrors(time);
    if (minutes !== 0) {
      const mixin: ParamsParticipantData = {};
      aliases.forEach(
        (alias) =>
          (mixin[alias] = {
            attendance: 'ATTENDED',
            trackedTime: minutes,
          })
      );
      updateParticipants(mixin);
      updateBulkParticipantsFromParticipantData(mixin);
      onDismiss(new CustomEvent<ModalProps.DismissDetail>('closeButton'));
    }
  };

  return {
    error,
    warning,
    pending: timeMutation.isPending,
    time,
    setTime,
    parseTimeWithWarningsAndErrors,
    updateAttendanceWithApi,
    updateAttendanceInUrl,
  };
}

export function TimeModal(props: TimeModalProps & ModalProps) {
  const { t } = useTranslation(['shifts', 'translation']);
  const user = useUser();

  const {
    error,
    warning,
    pending,
    time,
    setTime,
    parseTimeWithWarningsAndErrors,
    updateAttendanceWithApi,
    updateAttendanceInUrl,
  } = useUpdateAttendance(props.shift, props.onDismiss, props.aliases || [user.userId]);

  return (
    <Modal
      {...props}
      visible={props.visible}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              variant="link"
              onClick={() =>
                props.onDismiss(new CustomEvent<ModalProps.DismissDetail>('closeButton'))
              }
            >
              {t('timeModal.cancel')}
            </Button>
            <Button
              variant="primary"
              loading={pending}
              disabled={error !== '' || time === ''}
              data-aci-analytics-name="shifts_reportTime"
              onClick={props.saveToUrl ? updateAttendanceInUrl : updateAttendanceWithApi}
            >
              {t('timeModal.trackTime')}
            </Button>
          </SpaceBetween>
        </Box>
      }
      header={t('timeModal.title')}
      closeAriaLabel={t('timeModal.cancel')}
    >
      <form onSubmit={(e) => e.preventDefault()}>
        <Form>
          <FormField label={t('timeModal.timeVolunteered')} warningText={warning} errorText={error}>
            <Input
              onChange={({ detail }) => {
                // Strip out anything other than digits or :
                const rawTime = (detail.value.match(/[\d:]/g) || []).join('');
                setTime(rawTime);
                parseTimeWithWarningsAndErrors(rawTime);
              }}
              onBlur={() => parseTimeWithWarningsAndErrors(time)}
              value={time}
              placeholder="hh:mm"
              ariaRequired
              autoFocus={props.visible}
              inputMode="numeric"
              step={1}
            />
          </FormField>
        </Form>
      </form>
    </Modal>
  );
}
